import {Injectable} from '@angular/core';
import {HttpClient, HttpHandler, HttpInterceptor, HttpRequest} from "@angular/common/http";
import {Observable} from "rxjs";
import {tap} from "rxjs/operators";
import {
  DiscountBenefitType,
  DiscountConditionType,
  DiscountType, OrderChannel,
  OrderMethod,
  OrderStatus, PaymentType,
  TableType
} from "./constants";
import {Table} from "./models/table";

// strongly type some numbers
export type CityID = number;
export type WeekDay = number;  // 0=Sunday, 6=Saturday
export type Minutes = number;
export type Seconds = number;

/** the *JSON types describe our wire formats **/


export interface MenuJSON {
  id: number;
  name: string;
  text: string;
  theme: string;
  root_id: number;
}

export interface ConstraintJSON {
  days: Array<WeekDay>;
  allow_from: number;
  allow_to: number;
  allow_at: Date;
  allow_delivery: boolean;
  allow_takeout: boolean;
  allow_inhouse: boolean;
  tables: Array<string>;
  label: string;
}

export interface MenuGroupJSON {
  id: number;
  slug: string;
  name: string;
  text: string;
  active: boolean;
  parent: number;
  image: string;
  item_ids: Array<number>;
  search: string;
  hidden: boolean;
  children_ids: Array<number>;
  constraint?: ConstraintJSON;
}

export interface MenuItemJSON {
  id: number;
  slug: string;
  name: string;
  int_name: string;
  quantity: string;
  price: string;
  text: string;
  sort: number;
  allow_freetext: boolean;
  active: boolean;
  search: string;
  image: string;
  variants: Array<VariantJSON>;
  option_ids: Array<number>;
  footnote_ids: Array<number>;
  choices: Array<{id: number, name: string, group: number}>;
  constraint?: ConstraintJSON;
}

export interface VariantJSON {
  id: number;
  name: string;
  int_name: string;
  quantity: string;
  price: string;
  text: string;
  sort: number;
  active: boolean;
  footnote_ids: Array<bigint>;
}

export interface OptionJSON {
  id: number;
  name: string;
  int_name: string;
  quantity: string;
  price: string;
  text: string;
  sort: number;
  footnote_ids: Array<bigint>;
}

export interface FootnoteJSON {
  code: string;
  title: string;
  text: string;
}

export interface CityJSON {
  id: CityID;
  zip: string;
  city: string;
  min_amount: string;
  duration: Minutes;
  fee: string;
  waive_fee_amount: string;
}

export interface TableJSON {
  id: number;
  host_id: number;
  idtag: string;
  is_active: boolean;
  number: string;
  text: string;
  type: TableType;
}

export interface DiscountConditionJSON {
  type: DiscountConditionType;
  amount: string;
  order_method: string;
  payment_method: string;
  group_id: number;
  item_id: number;
  from_hour: string;
  to_hour: string;
  count: number;
}

export interface DiscountBenefitJSON {
  id: number;
  type: DiscountBenefitType;
  amount: string;
  percentage: string;
  item_id: number;
  group_id: number;
  count: number;
}

export interface DiscountJSON {
  type: DiscountType;
  title: string;
  text: string;
  code: string;
  promote_text: string;
  app_only: boolean;
  multiple: boolean;
  conditions: Array<DiscountConditionJSON>;
  benefits: Array<DiscountBenefitJSON>;
}

export interface CurrencyFormatJSON {
  decimal: string;
  group: string;
  prefix: [string, string];
  suffix: [string, string];
}

export interface UserJSON {
  id: number,
  username: string,
  first_name: string,
  last_name: string,
  email: string,
  is_staff: boolean,
  is_waiter: boolean
}

export interface HostJSON {
  id: number;
  name: string;
  text: string;
  email: string;
  phone: string;
  domain: string;
  address: string;
  greeting: string;
  payment_options: PaymentOptionJSON[];
  homepage: string;
  facebook_page: string;
  return_page: string;
  logo: string;
  session_maxage: number;
  allow_waiter_call: boolean;
  allow_order: boolean;
  merge_orders: boolean;
  merge_positions: boolean;
  simple_navigation: boolean;
  has_delivery: boolean;
  has_takeout: boolean;
  delivery_cities: Array<CityJSON>;
  discounts: Array<DiscountJSON>;
  open_times: [string, string];
  require_delivery_phone: boolean;
  require_delivery_email: boolean;
  takeout_preparation_time: Minutes;
  preorder_days: number;
  preorder_times: Array<{day: number, label: string, times: [{ start: number, end: number }]}>;
  order_note_1: string;
  order_note_2: string;
  contact_data_inhouse: boolean;
  flat_mode: boolean;
  has_discount_codes: boolean;
  matomo_id: string;
  timezone: string;
  utc_offset: number;
  show_footer: boolean;
  show_footer_terms: boolean;
  currency: string;
  currency_symbol: string;
  currency_format: CurrencyFormatJSON;
  i18n: {[id: string]: string};
  feature_flags:string[];
  vat_groups: Array<VatGroupJSON>;
}

export interface VatGroupJSON {
  id: number;
  name: string;
}

export interface T4DDataJSON {
  timeout: number;
  host: HostJSON;
  table: TableJSON;
  tables: Array<TableJSON>;
  menu: MenuJSON;
  allow_order: boolean;
  allow_waiter: boolean;
  csrf_token: string;
  items: {[id: number]: MenuItemJSON};
  groups: {[id: number]: MenuGroupJSON};
  options: {[id: number]: OptionJSON};
  footnotes: {[id: number]: FootnoteJSON};
  user?: UserJSON;
}

export interface PaymentOptionJSON {
  slug: string;
  type: PaymentType;
  icon: string;
  label: string;
  text: string;
  selected_text: string;
  discounts: string[];
}

export interface CartEntryJSON {
  count: number;
  item_id: number;
  variant_id: number;
  option_ids: Array<number>;
  title: string;
  price: number;
  choices: {[id: number]: string};
  choices_txt: string;
  choices_price: number;
  comment: string;
  vat_group: number;
}

export interface DeliveryAddressJSON {
  name: string;
  address: string;
  email: string;
  phone: string;
  city: number;
  notes: string;
}

export interface BenefitSelectionJSON {
  benefit_id: string;
  item_id: number;
  variant_id: number;
}

export interface CartJSON {
  address: DeliveryAddressJSON;
  positions: Array<CartEntryJSON>;
  payment: string;                          // payment method
  method: OrderMethod;                      // inhouse, delivery or takeout (same as TableType)
  desired_time: number;                     // requested delivery time as unix ts (epoch milliseconds)
  selections: Array<BenefitSelectionJSON>;  // benefit selection
  codes: Array<string>;                     // entered discount/voucher codes
  waivedDiscounts?: Array<string>;          // waived discounts (host-order only)
  status?: OrderStatus;                     // order status (host-order only)
  channel?: OrderChannel;                   // ordering channel
}

export interface OrderResponseJSON {
  orderId: number;
  orderNumber: string;
  orderTotal: string;
  updateToken: string;
  orderMethod: OrderMethod;
  estimatedTime: string;
  redirect: string;
}

export interface UpdateOrderJSON {
  id: number;
  token: string;
  status: string;
  payment: string;
}

export interface PageJSON {
  title: string;
  content: string;
}

/** the actual backend service wraps all http operations **/

@Injectable({ providedIn: 'root' })
export class BackendService {
  backend: string = null;
  idtag: string = null;
  asset_base: string = '';
  csrf_token: string = null;
  user: string = null;      // basic auth (ATM only for devserver)
  password: string = null;  // basic auth (ATM only for devserver)

  constructor(
    private http: HttpClient
  ) {}

  setBackend(backend: string) {
    this.backend = backend;
  }

  setAssetBase(asset_base: string) {
    this.asset_base = asset_base;
  }

  setIdTag(idtag: string) {
    this.idtag = idtag;
  }

  getTableUrl(table?:Table): string {
    const idtag = (table) ? table.idtag : this.idtag,
          url = this.backend + idtag + '/';
    return url + ((window['t4dFakeTime'])? '?fake_time=' + window['t4dFakeTime'].toString() : '');
  }

  getPaymentMethodUrl(table?:Table): string {
    return '/payment/methods/' + ((table) ? table.idtag : this.idtag) + '/';
  }

  getCodeUrl(table?:Table): string {
    const idtag = (table) ? table.idtag : this.idtag;
    return this.backend + idtag + '/code/';
  }

  getOrderUrl(table?:Table): string {
    return this.getTableUrl(table);
  }

  getPageUrl(page: string): string {
    return this.backend + '-page-/' + page + '/';
  }

  getCSRFCookie(name: string = 'csrftoken'): string {
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
      const cookies = document.cookie.split(';');
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i].trim();
        // Does this cookie string begin with the name we want?
        if (cookie.substring(0, name.length + 1) === (name + '=')) {
          cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
          break;
        }
      }
    }
    return cookieValue;
  }

  getT4DData(): Observable<T4DDataJSON> {
    return this.http.get<T4DDataJSON>(this.getTableUrl())
      .pipe(tap<T4DDataJSON>( data => {
        this.csrf_token = data.csrf_token;
      }))
  }

  getPaymentOptions(cart: CartJSON): Observable<PaymentOptionJSON[]> {
    return this.http.post<PaymentOptionJSON[]>(
      this.getPaymentMethodUrl(),
      cart,
      {headers: {'X-CSRFToken': this.getCSRFCookie()}})
  }

  sendOrder(cart: CartJSON, table?:Table): Observable<OrderResponseJSON> {
    return this.http.post<OrderResponseJSON>(
      this.getOrderUrl(table),
      cart,
      {headers: {'X-CSRFToken': this.getCSRFCookie()}}
    );
  }

  updateOrder(data: UpdateOrderJSON) {
    return this.http.patch<OrderResponseJSON>(
      this.getOrderUrl(),
      data,
      {headers: {'X-CSRFToken': this.csrf_token}}
    );
  }

  sendServiceCall(table: Table): Observable<void> {
    return this.http.post<void>(
      this.getOrderUrl(table), {
        type: 'servicecall'
      },
      {headers: {'X-CSRFToken': this.csrf_token}}
    );
  }

  checkCode(code: string): Observable<DiscountJSON> {
    return this.http.post<DiscountJSON>(
      this.getCodeUrl(),
      {'code': code},
      {headers: {'X-CSRFToken': this.csrf_token}}
    )
  }

  getPage(page: string): Observable<PageJSON> {
    return this.http.get<PageJSON>(
      this.getPageUrl(page)
    )
  }

}
